import type Vue from 'vue'
import type { Location } from 'vue-router/types/router'
import type { FeatureNode, FeaturesConfig } from '../../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { getCurrentInstance } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum } from '../../enums'

export interface FeatureRoutesComposable {
  getFeatureRoutes: (featureId: FeatureIdEnum) => Record<string, string> | undefined
  getUpdateLocation: ({ id, feature }: { id: string; feature: FeatureNode }) => Location
  getCreateLocation: (feature: FeatureNode) => Location
  getListLocation: ({ feature, query }: { feature: FeatureNode; query: string }) => Location
  isFeatureConfigurableByQuery: (feature: FeatureNode) => boolean
}

export const useFeatureRoutes = (): FeatureRoutesComposable => {
  const {
    proxy: { $router },
  } = getCurrentInstance() as { proxy: Vue }

  const isFeatureConfigurableByQuery = (feature: FeatureNode): boolean => {
    return (
      feature.parentId === 'query' ||
      feature.id === FeatureIdEnum.SYNONYM ||
      feature.id === FeatureIdEnum.CONFIGURATION
    )
  }

  const getFeatureRoutes = (featureId: FeatureIdEnum): Record<string, string> | undefined => {
    const featuresConfig = CONTAINER.get<FeaturesConfig>(TYPES.FeaturesConfig)
    const featureConfig = featuresConfig[DomainEnum.SEARCH].find(config => config.id === featureId)
    return featureConfig?.routeNameBase
  }

  const getUpdateLocation = ({ id, feature }: { id: string; feature: FeatureNode }): Location => {
    return {
      name: feature.routeNameBase?.update ?? '',
      params: {
        instanceId: $router.currentRoute.params.instanceId,
        id,
      },
    }
  }

  const getCreateLocation = (feature: FeatureNode): Location => {
    return {
      name: feature.routeNameBase?.create ?? '',
      params: {
        instanceId: $router.currentRoute.params.instanceId,
      },
    }
  }

  const getListLocation = ({
    feature,
    query,
  }: {
    feature: FeatureNode
    query: string
  }): Location => {
    return {
      name: feature.routeName,
      params: {
        instanceId: $router.currentRoute.params.instanceId,
      },
      ...(isFeatureConfigurableByQuery(feature) && { query: { q: query } }),
    }
  }

  return {
    getFeatureRoutes,
    getUpdateLocation,
    getCreateLocation,
    getListLocation,
    isFeatureConfigurableByQuery,
  }
}
